/**
 * Rename a single campaign — modal C from the 2026-05-06 design
 * handoff (`campaign_modals.html`). Visible only when exactly one
 * campaign is selected; opened from the BulkActions Actions
 * dropdown's "Rename Campaign" item.
 *
 * Pipeline (matches the rest of Ads Manager): on Apply we hit
 * PATCH /api/campaigns/{id} → backend writes a pending_writes row
 * → drainer pushes to ASA → confirms → toast. The optimistic mirror
 * patch happens server-side; the modal closes immediately on
 * mutate-fire so the user can keep going. Failures (incl. the
 * 422 'campaign_name_already_in_use' guard) surface inline before
 * the close so the user can correct without re-opening.
 */

import { useEffect, useState } from "react";
import { useDiscardGuard } from "@/lib/discard-guard";
import { I } from "@/components/Icon";

export type RenameCampaignModalProps = {
  open: boolean;
  onClose: () => void;
  campaign: { campaign_id: number; name: string } | null;
  /** Fire when the user clicks Apply with a valid (non-empty,
   *  changed) name. Parent owns the mutation + toast. */
  onApply: (
    campaign_id: number,
    update: { name: string },
  ) => Promise<void>;
};

export function RenameCampaignModal({
  open,
  onClose: onCloseProp,
  campaign,
  onApply,
}: RenameCampaignModalProps) {
  const [newName, setNewName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Reset on open: pre-fill input with the current name + autofocus
  // happens via the autoFocus attr below.
  useEffect(() => {
    if (open && campaign) {
      setNewName(campaign.name);
      setError(null);
      setBusy(false);
    }
  }, [open, campaign]);

  const guard = useDiscardGuard(open && !!campaign, !!campaign && newName.trim() !== campaign.name, onCloseProp);
  const onClose = guard.requestClose;

  if (!open || !campaign) return null;

  const trimmed = newName.trim();
  const isDirty = trimmed.length > 0 && trimmed !== campaign.name;
  const tooLong = trimmed.length > 240;
  const canApply = isDirty && !tooLong && !busy;

  const handleApply = async () => {
    if (!canApply) return;
    setError(null);
    setBusy(true);
    try {
      await onApply(campaign.campaign_id, { name: trimmed });
      onCloseProp();
    } catch (err) {
      // Surface backend errors inline (e.g. 422
      // campaign_name_already_in_use) instead of relying on the
      // global toast — local context is more useful here.
      const detail =
        (err as {
          response?: {
            data?: { error?: { message?: string } };
            status?: number;
          };
        })?.response?.data?.error?.message ?? "Couldn't rename campaign.";
      setError(detail);
      setBusy(false);
    }
  };

  return (
    <div
      className="cm-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="cm-shell"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rename-title"
      >
        <div className="cm-head">
          <div>
            <div className="cm-title" id="rename-title">
              Rename <em>campaign</em>
            </div>
            <div className="cm-sub">
              Renaming changes how this campaign appears across the
              dashboard, rules and reports. Existing data and history
              are preserved.
            </div>
          </div>
          <button className="cm-x" aria-label="Close" onClick={onClose}>
            {I.x}
          </button>
        </div>

        <div className="cm-body">
          <div className="cm-field">
            <label className="cm-field-label">Current name</label>
            <input
              className="cm-field-input"
              type="text"
              value={campaign.name}
              disabled
              readOnly
            />
          </div>
          <div className="cm-field">
            <label className="cm-field-label" htmlFor="rename-new-name">
              New name
            </label>
            <input
              id="rename-new-name"
              className="cm-field-input"
              type="text"
              value={newName}
              autoFocus
              onChange={(e) => {
                setNewName(e.target.value);
                if (error) setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleApply();
              }}
              maxLength={300}
            />
            {error ? (
              <div className="cm-field-error">{error}</div>
            ) : tooLong ? (
              <div className="cm-field-error">
                Name is over 240 characters ({trimmed.length}).
              </div>
            ) : (
              <div className="cm-field-hint">
                Up to 240 characters · must be unique within the app
              </div>
            )}
          </div>
        </div>

        <div className="cm-foot">
          <button className="cm-btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="cm-btn primary"
            onClick={handleApply}
            disabled={!canApply}
          >
            {busy ? "Applying…" : "Apply"}
          </button>
        </div>
      </div>
    </div>
  );
}
